/**
 * Fleets Domain
 * Types, store, handlers, and service methods for fleet management
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { OpCode, MessageType } from '../constants/op-codes';
import { FLEET_SLOT_NAME_MAX_LENGTH } from '../constants/server-fleets';
import { sendGatedCommand } from './command-gate';
import type { Team } from './teams';
import type { Segment } from './segments';
import { airplanes, type AirplaneModel } from '../data/airplanes';
import { compileCabinConfig, defaultCabinConfig, type CabinConfig, type CompiledCabinMetrics } from '../data/cabin';
import { showErrorToast } from './toasts';
import i18n from '../i18n/config';
import { getCurrentCohortId, useCurrentCohortId } from './current-team';
import { budgetBlock, cashBlock, refusalKey, refusalText, type OrderBlock, type OrderRefusal } from './order-block';
import { formatNumber } from '../utils/format';
import { handleSegmentUpdate } from './segments';
import { handleTeamUpdate } from './teams';

// ==================== TYPES ====================

/**
 * PlaneSlotState represents the availability and current version of a plane slot.
 * Tracks unlocked status, current model variant, and the cost (which may include modifiers).
 */
interface PlaneSlotState {
  /** Whether this plane slot is available to the team */
  unlocked: boolean;
  /** Model number of the currently available variant (v1, v2, or v3) */
  current_model: number;
  /** Purchase price for this team (can be modified by manufacturer_relations deal) */
  cost: number;
}

/**
 * AvailablePlanes tracks which plane models are available to a team.
 * Contains 8 slots (one per base model category: Sparrow, Swift, Hawk, Eagle, Condor, Pelican, Falcon, Osprey).
 */
export interface AvailablePlanes {
  /** Array of 8 plane slot states, indexed by base model (0-7) */
  slots: PlaneSlotState[];
}

/**
 * FleetSlot is one independent cabin configuration of an aircraft model.
 * Contains the customized seat layout. Aircraft performance specs are
 * looked up from AirplaneModel via getAirplaneModelForSlot(slot).
 */
export interface FleetSlot {
  /** Player-authored label. Empty means unnamed - see getFleetSlotLabel(). */
  name?: string;
  /** BaseModel is the model number from airplanes.json (e.g., 0, 1, 2, 7, 8...) */
  base_model: number;
  /** CabinConfig is the editor-facing cabin layout */
  cabin_config?: CabinConfig;
  /** Compiled cabin metrics are the server-authoritative aggregate values */
  compiled?: CompiledCabinMetrics;
  /** Seats is an array of seat counts [economy, business, first] */
  seats: [number, number, number];
  /** Total is the total number of this configuration in the fleet */
  total: number;
  /** Unassigned is the number not yet assigned to routes */
  unassigned: number;
  /** Age buckets track aircraft inventory by purchase quarter */
  age_buckets?: FleetAgeBucket[];
}

/**
 * FleetAgeBucket tracks aircraft age and cost basis for depreciation and sales.
 */
interface FleetAgeBucket {
  purchase_quarter: number;
  unit_cost: number;
  total: number;
  unassigned: number;
}

/**
 * Fleet represents a team's entire collection of aircraft.
 * Slots is a flat, fixed-length list: a slot's position is its identity, and
 * every message addressing a configuration does so by that index.
 */
export interface Fleet {
  /** TeamID is the team that owns this fleet */
  team_id: number;
  /** MaxPlanes is the maximum number of aircraft this fleet can hold.
   *  This caps aircraft, not configurations - the two limits are independent. */
  max_planes: number;
  /** Slots holds the team's FLEET_SLOT_COUNT configurations. An empty slot is a
   *  zero value; removal clears in place so slot indices never shift. */
  slots: FleetSlot[];
}

/**
 * Get the total number of aircraft in a fleet across all configurations
 */
export function getTotalPlanes(fleet: Fleet | null | undefined): number {
  if (!fleet?.slots) return 0;

  let total = 0;
  for (const slot of fleet.slots) {
    if (slot?.total) total += slot.total;
  }
  return total;
}

/**
 * Get the total number of unassigned aircraft in a fleet across all configurations
 */
export function getTotalUnassignedPlanes(fleet: Fleet | null | undefined): number {
  if (!fleet?.slots) return 0;

  let unassigned = 0;
  for (const slot of fleet.slots) {
    if (slot?.unassigned) unassigned += slot.unassigned;
  }
  return unassigned;
}

/**
 * Get the AirplaneModel for a fleet slot.
 * This is the single source of truth for aircraft performance characteristics.
 * @param slot - The FleetSlot to get the model for
 * @returns The AirplaneModel or undefined if not found
 */
export function getAirplaneModelForSlot(slot: FleetSlot | null | undefined): AirplaneModel | undefined {
  if (!slot) return undefined;
  return airplanes.getByModelNumber(slot.base_model);
}

export function getFleetSlotRange(slot: FleetSlot | null | undefined): number {
  if (!slot) return 0;
  if (slot.compiled?.configured_range) return slot.compiled.configured_range;
  const model = airplanes.getByModelNumber(slot.base_model);
  return model?.range ?? 0;
}

/**
 * A configuration's range and seats as its cabin makes them — the figures an
 * aircraft card draws, where the model's own maxima would overstate a cabin
 * of wide seats.
 */
export function configurationFigures(slot: FleetSlot): { range: number; seats: number } {
  const seats = slot.compiled?.seats_by_slot ?? slot.seats;
  return { range: getFleetSlotRange(slot), seats: seats[0] + seats[1] + seats[2] };
}

/**
 * The longest range among the aircraft a team actually owns. Used to decide how
 * far the globe should offer to reach.
 */
function getFleetMaxRange(fleet: Fleet | null | undefined): number {
  if (!fleet?.slots) return 0;

  let maxRange = 0;
  for (const slot of fleet.slots) {
    if (!slot || !slot.total || isFleetSlotEmpty(slot)) continue;
    maxRange = Math.max(maxRange, getFleetSlotRange(slot));
  }

  return maxRange;
}

/** The server permits first investments within a purchasable default Sparrow's range. */
export function getInvestmentAircraftRange(fleet: Fleet | null | undefined): number {
  const sparrow = airplanes.getByModelNumber(0);
  const starterRange = sparrow ? compileCabinConfig(sparrow, defaultCabinConfig(sparrow)).metrics?.configured_range ?? sparrow.range : 0;
  return Math.max(starterRange, getFleetMaxRange(fleet));
}

/**
 * Check if a fleet slot is empty (no seats configured).
 * Note: base_model 0 is valid (Ponder Sparrow), so we check seats instead.
 * @param slot - The FleetSlot to check
 * @returns true if the slot is empty
 */
export function isFleetSlotEmpty(slot: FleetSlot | null | undefined): boolean {
  if (!slot) return true;
  const seats = slot.compiled?.seats_by_slot ?? slot.seats;
  return seats[0] === 0 && seats[1] === 0 && seats[2] === 0 && (slot.cabin_config?.editor_blocks?.length ?? 0) === 0;
}

/** What can happen to a sub-fleet that the team's activity says. */
type FleetSlotChange = 'added' | 'removed' | 'updated';

/**
 * The sub-fleet a fleet's change touched, by the name its line gives it, or
 * null where it touched none that way: one configured where its slot was
 * empty (added), one emptied (removed), or one still configured, reseated or
 * renamed (updated). Empty is isFleetSlotEmpty's word — no cabin configured —
 * never the model: base_model 0 is the first model, the Sparrow, as much as it
 * is an empty slot's zero value, so a Sparrow sub-fleet added, removed or
 * changed is told like any other.
 */
export function fleetSlotChange(before: Fleet | undefined, after: Fleet | undefined, change: FleetSlotChange): string | null {
  if (!after) return null;
  const was = before?.slots ?? [];
  const is = after.slots ?? [];
  for (let i = 0; i < Math.max(was.length, is.length); i++) {
    const configuredBefore = !isFleetSlotEmpty(was[i]);
    const configuredAfter = !isFleetSlotEmpty(is[i]);
    if (change === 'added' && !configuredBefore && configuredAfter) return getFleetSlotLabel(is[i], i);
    if (change === 'removed' && configuredBefore && !configuredAfter) return getFleetSlotLabel(was[i], i);
    if (change === 'updated' && configuredBefore && configuredAfter) {
      const reseated = JSON.stringify(was[i].seats) !== JSON.stringify(is[i].seats);
      const renamed = (was[i].name ?? '') !== (is[i].name ?? '');
      if (reseated || renamed) return getFleetSlotLabel(is[i], i);
    }
  }
  return null;
}

/**
 * The configured slot at an index, or null when the index is out of range or
 * the slot holds nothing. Every screen that resolves a segment's slot goes
 * through here, so none of them has to remember that a slot can exist and
 * still be empty.
 */
export function getFleetSlot(fleet: Fleet | null | undefined, slotIndex: number): FleetSlot | null {
  const slot = fleet?.slots?.[slotIndex];
  if (!slot || isFleetSlotEmpty(slot)) return null;
  return slot;
}

/**
 * The name a slot starts with: the aircraft and the slot's position.
 * Trimmed to fit FLEET_SLOT_NAME_MAX_LENGTH, since the longest aircraft names
 * do not fit the cap once the number is appended. The position keeps two slots
 * of the same aircraft apart, so it is the part that survives the trim.
 */
export function buildDefaultSlotName(modelName: string, slotIndex: number): string {
  const suffix = ` ${slotIndex + 1}`;
  const room = FLEET_SLOT_NAME_MAX_LENGTH - suffix.length;
  const base = modelName.length > room ? modelName.slice(0, room).trimEnd() : modelName;
  return `${base}${suffix}`;
}

/**
 * What to call a slot on screen. The player's own label wins; a slot saved
 * without one falls back to the same default the editor would have offered.
 */
export function getFleetSlotLabel(slot: FleetSlot | null | undefined, slotIndex: number): string {
  const name = slot?.name?.trim();
  if (name) return name;

  const model = getAirplaneModelForSlot(slot);
  return model ? buildDefaultSlotName(model.model_name, slotIndex) : `${slotIndex + 1}`;
}

/** The aircraft a sub-fleet flies: the model, the sub-fleet and that sub-fleet's name. */
export interface Aircraft {
  model: AirplaneModel;
  slot: FleetSlot;
  /** The sub-fleet's name. */
  name: string;
}

/**
 * The aircraft of a fleet's sub-fleet `slotIndex`, or null while the fleet is
 * not known, or the sub-fleet holds no configuration or a model the client
 * does not know: how a flight's page and a refusal of a flight name it.
 */
export function aircraftOf(fleet: Fleet | null | undefined, slotIndex: number): Aircraft | null {
  const slot = getFleetSlot(fleet, slotIndex);
  const model = getAirplaneModelForSlot(slot);
  if (!slot || !model) return null;
  return { model, slot, name: getFleetSlotLabel(slot, slotIndex) };
}

/**
 * What the team pays for one aircraft of a model today: the price a deal has
 * cut, or the list price. Prices are kept by family — the model's own
 * `base_model`, 0–7 — and never by a fleet slot's `base_model`, which is a
 * model number: slot 0 of the prices is every Sparrow, Mark II included.
 */
export function teamUnitPrice(model: AirplaneModel, prices: AvailablePlanes | null | undefined): number {
  return prices?.slots?.[model.base_model]?.cost ?? model.cost;
}

/**
 * How many distinct aircraft families the fleet operates. This is the
 * fleet-diversity measure: several cabin configurations of one aircraft are one
 * family, and so are a Hawk v1 and its Hawk XL upgrade, since both share an
 * AirplaneModel.base_model. Counting occupied slots instead would penalise a
 * standardised fleet and make the diversity mission trivial.
 * Mirrors Fleet.DistinctModelFamilies in fleets.go.
 */
export function getDistinctModelFamilies(fleet: Fleet | null | undefined): number {
  if (!fleet?.slots) return 0;

  const families = new Set<number>();
  for (const slot of fleet.slots) {
    if (!slot || isFleetSlotEmpty(slot) || !slot.total) continue;
    const model = getAirplaneModelForSlot(slot);
    if (!model) continue;
    families.add(model.base_model);
  }
  return families.size;
}

// ==================== STORE ====================

interface FleetsState {
  fleets: Record<string, Fleet>; // Map of cohort_id -> Fleet
  availablePlanes: Record<string, AvailablePlanes>; // Map of cohort_id -> AvailablePlanes
  setFleets: (fleets: Record<string, Fleet>) => void;
  updateFleet: (cohortId: string, fleet: Fleet) => void;
  getFleetByCohortId: (cohortId: string) => Fleet | undefined;
  setAvailablePlanes: (cohortId: string, planes: AvailablePlanes) => void;
  reset: () => void;
}

const initialState = {
  fleets: {},
  availablePlanes: {} as Record<string, AvailablePlanes>,
};

/**
 * Fleets Store
 * Manages fleet state for the current match
 */
export const useFleetsStore = create<FleetsState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      /**
       * Set fleets data (replaces existing)
       */
      setFleets: (fleets) => {
        console.log('[FleetsStore] Setting fleets data:', Object.keys(fleets).length, 'fleets');
        set({ fleets }, false, 'setFleets');
      },

      /**
       * Update a specific fleet
       */
      updateFleet: (cohortId, fleet) => {
        console.log('[FleetsStore] Updating fleet:', cohortId);
        set(
          (state) => ({
            fleets: {
              ...state.fleets,
              [cohortId]: fleet,
            },
          }),
          false,
          'updateFleet'
        );
      },

      /**
       * Get fleet by cohort ID
       */
      getFleetByCohortId: (cohortId) => {
        return get().fleets[cohortId];
      },

      /**
       * Set available planes for a cohort (includes modified costs from deals like manufacturer_relations)
       */
      setAvailablePlanes: (cohortId, planes) => {
        console.log('[FleetsStore] Setting available planes for cohort:', cohortId, planes);
        set(
          (state) => ({
            availablePlanes: {
              ...state.availablePlanes,
              [cohortId]: planes,
            },
          }),
          false,
          'setAvailablePlanes'
        );
      },

      /**
       * Reset the fleets store to initial state
       */
      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    {
      name: 'fleets-store',
    }
  )
);

/** The viewer's team's fleet; undefined until the player list and the fleets have arrived. */
export function useCurrentFleet(): Fleet | undefined {
  const cohortId = useCurrentCohortId();
  return useFleetsStore((s) => (cohortId ? s.fleets[cohortId] : undefined));
}

/**
 * The viewer's team's fleet, for code outside React — a handler naming a
 * sub-fleet; undefined before the session and player data have arrived. In a
 * component, useCurrentFleet.
 */
export function getCurrentFleet(): Fleet | undefined {
  const cohortId = getCurrentCohortId();
  return cohortId ? useFleetsStore.getState().getFleetByCohortId(cohortId) : undefined;
}

// ==================== REFUSALS ====================

/**
 * The reasons the server refuses an order for aircraft or a change to a
 * sub-fleet for, where a player can meet them — fleets.go and spending-lock.go
 * send each as a code beside the English — by the key that says each in the
 * player's language. A Go test reads this table (fleet_refusal_test.go), so the
 * two sides name the same codes. `cash` and `budget` carry the purchase's cost
 * and what the team's cash or the buyer's budget holds, in millions, and are
 * said by cashBlock and budgetBlock, as the Buy button says them: their keys
 * here are those blocks' own, listed so the codes are named. `out_of_range`
 * carries the range that keeps the longest route the sub-fleet flies and the
 * new cabin's, in km; `duplicate` the slot of the sub-fleet that already holds
 * the cabin.
 */
const FLEET_REFUSALS = {
  already_set_up: 'fleet.refusal.alreadySetUp',
  budget: 'common.budgetReason',
  cash: 'common.cashReason',
  cash_locked: 'common.cashLocked',
  duplicate: 'fleet.refusal.duplicate',
  has_aircraft: 'fleet.refusal.hasAircraft',
  no_idle_aircraft: 'fleet.refusal.noIdleAircraft',
  not_set_up: 'fleet.refusal.notSetUp',
  out_of_range: 'fleet.refusal.outOfRange',
} as const;

/** A refused order or change, as it arrives. */
export type FleetRefusal = OrderRefusal & {
  /** A duplicate's: the slot of the sub-fleet that already holds the cabin. */
  conflict_slot_index?: number;
};

/**
 * What a refused order or change tells the player: the key that says its
 * reason, with its figures in the player's number style, or the name of the
 * sub-fleet in `fleet` that already holds the cabin. Null when the server sent
 * no reason the client knows — a failure no player meets in play, or a code
 * from a newer server — or a reason that lost its figures, and the server's
 * words stand.
 */
export function readFleetRefusal(
  { reason, need, have, conflict_slot_index: conflict }: FleetRefusal,
  fleet?: Fleet | null,
): OrderBlock | null {
  const key = refusalKey(FLEET_REFUSALS, reason);
  if (key === undefined) return null;
  if (reason === 'duplicate') {
    if (typeof conflict !== 'number') return null;
    return { key, values: { slot: getFleetSlotLabel(fleet?.slots?.[conflict], conflict) } };
  }
  if (reason !== 'cash' && reason !== 'budget' && reason !== 'out_of_range') return { key };
  if (typeof need !== 'number' || typeof have !== 'number') return null;
  if (reason === 'out_of_range') return { key, values: { need: formatNumber(need), range: formatNumber(have) } };
  return reason === 'cash' ? cashBlock(have, need) : budgetBlock(have, need);
}

/** A refused order or change, in a toast: what failed, and why. */
function showFleetRefusal(title: string, refusal: FleetRefusal, fleet?: Fleet): void {
  // Non-blocking toast — never alert(), which freezes the game.
  showErrorToast(title, refusalText(readFleetRefusal(refusal, fleet), refusal));
}

// ==================== HANDLERS ====================

/**
 * Handle OpCodeFleetsState message
 * Sets the complete fleets state for the match
 */
export function handleFleetsState(data: { 
  fleets: Record<string, Fleet>;
}) {
  console.log('[FleetsHandler] Handling fleets state:', Object.keys(data.fleets || {}).length, 'fleets');
  
  const store = useFleetsStore.getState();
  
  // Set fleets
  if (data.fleets) {
    store.setFleets(data.fleets);
  }
}

/**
 * Handle OpCodeAvailablePlanesState message
 * Sets available planes for a team (includes modified costs from deals like manufacturer_relations)
 */
export function handleAvailablePlanesState(data: { cohort_id: string; available_planes: AvailablePlanes }) {
  console.log('[FleetsHandler] Handling available planes state for cohort:', data.cohort_id);
  handleAvailablePlanesUpdate(data.cohort_id, data.available_planes);
}

/**
 * A team's available planes as a message carries them: their own state
 * message, or a deal an innovation struck on their costs (innovation.ts).
 */
export function handleAvailablePlanesUpdate(cohortId: string, availablePlanes: AvailablePlanes): void {
  useFleetsStore.getState().setAvailablePlanes(cohortId, availablePlanes);
}

/**
 * A team's fleet as another domain's message carries it: a flight added or
 * removed assigned or freed its planes (segments.ts), a plane innovation
 * upgraded its sub-fleets (innovation.ts).
 */
export function handleFleetUpdate(cohortId: string, fleet: Fleet): void {
  useFleetsStore.getState().updateFleet(cohortId, fleet);
}

/**
 * Handle OpCodeAddFleetSlotSuccess message
 * Updates the fleet after a slot is added
 */
export function handleAddFleetSlotSuccess(data: { fleet: Fleet; cohort_id: string; sender_user_id?: string }) {
  console.log('[FleetsHandler] Fleet slot added:', data.cohort_id);
  useFleetsStore.getState().updateFleet(data.cohort_id, data.fleet);
}

/**
 * Handle OpCodeRemoveFleetSlotSuccess message
 * Updates the fleet after a slot is removed
 */
export function handleRemoveFleetSlotSuccess(data: { fleet: Fleet; cohort_id: string; sender_user_id?: string }) {
  console.log('[FleetsHandler] Fleet slot removed:', data.cohort_id);
  useFleetsStore.getState().updateFleet(data.cohort_id, data.fleet);
}

/**
 * Handle OpCodeUpdateFleetSlotSuccess message
 * Updates the fleet after a slot is updated, and updates any affected segments
 */
export function handleUpdateFleetSlotSuccess(data: { fleet: Fleet; segments?: Segment[]; cohort_id: string; sender_user_id?: string }) {
  console.log('[FleetsHandler] Fleet slot updated:', data.cohort_id);
  useFleetsStore.getState().updateFleet(data.cohort_id, data.fleet);

  // Update affected segments (seat configuration changed): each one's load
  // factor is stale until the next tick.
  if (data.segments && data.segments.length > 0) {
    console.log('[FleetsHandler] Updating', data.segments.length, 'affected segments');
    for (const segment of data.segments) handleSegmentUpdate(segment, { stale: true });
  }
}

/**
 * Handle OpCodeAddFleetSlotFailed, OpCodeRemoveFleetSlotFailed and
 * OpCodeUpdateFleetSlotFailed messages
 * Logs error and shows user notification when a sub-fleet change is refused
 */
export function handleFleetOperationFailed(data: FleetRefusal, operation: string) {
  console.error(`[FleetsHandler] ❌ ${operation} failed:`, data.reason ?? '', data.error);
  // Failures are sent only to the requester, so the sub-fleet a duplicate
  // names is in the local player's own fleet.
  showFleetRefusal(i18n.t('fleet.operationFailed'), data, getCurrentFleet());
}

/**
 * Handle OpCodePurchaseAirplanesSuccess message
 * Updates the fleet after airplanes are purchased, and updates team cash
 */
export function handlePurchaseAirplanesSuccess(data: { fleet: Fleet; team: Team; cohort_id: string }) {
  console.log('[FleetsHandler] Airplanes purchased successfully:', data.cohort_id);
  
  // Update the fleet (Total and Unassigned fields increased)
  useFleetsStore.getState().updateFleet(data.cohort_id, data.fleet);
  
  // Update team cash
  handleTeamUpdate(data.cohort_id, data.team);
}

/**
 * Handle OpCodePurchaseAirplanesFailed message
 * Logs error and shows user notification when purchasing airplanes fails
 */
export function handlePurchaseAirplanesFailed(data: OrderRefusal) {
  console.error('[FleetsHandler] ❌ Purchase airplanes failed:', data.reason ?? '', data.error);
  showFleetRefusal(i18n.t('fleet.purchaseFailed'), data);
}

/**
 * Handle OpCodeSellAirplanesSuccess message
 * Updates the fleet after airplanes are sold, and updates team cash
 */
export function handleSellAirplanesSuccess(data: { fleet: Fleet; team: Team; cohort_id: string; proceeds: number }) {
  console.log('[FleetsHandler] Airplanes sold successfully:', data.cohort_id);

  // Update the fleet (Total and Unassigned fields decreased)
  useFleetsStore.getState().updateFleet(data.cohort_id, data.fleet);

  // Update team cash
  handleTeamUpdate(data.cohort_id, data.team);
}

/**
 * Handle OpCodeSellAirplanesFailed message
 * Logs error and shows user notification when selling airplanes fails
 */
export function handleSellAirplanesFailed(data: OrderRefusal) {
  console.error('[FleetsHandler] ❌ Sell airplanes failed:', data.reason ?? '', data.error);
  showFleetRefusal(i18n.t('fleet.sellFailed'), data);
}

// ==================== SERVICE METHODS ====================

/**
 * Add a new fleet slot configuration in the slot the player picked.
 * Server will extract cohort_id from player data (server-authoritative)
 * @param slotIndex - Which slot to write into (0-based)
 * @param modelNumber - The airplane model number
 * @param name - Player-authored label; empty is allowed
 * @param cabinConfig - The cabin layout to compile
 */
export async function addFleetSlot(
  slotIndex: number,
  modelNumber: number,
  name: string,
  cabinConfig: CabinConfig
): Promise<boolean> {
  const message = {
    type: MessageType.MessageTypeAddFleetSlot,
    slot_index: slotIndex,
    model_number: modelNumber,
    name,
    cabin_config: cabinConfig,
  };

  console.log('[FleetService] Adding fleet slot', slotIndex, 'model:', modelNumber);

  return sendGatedCommand(OpCode.OpCodeAddFleetSlot, JSON.stringify(message), { notSent: (refusal) => handleFleetOperationFailed(refusal, 'Add fleet slot') });
}

/**
 * Remove a fleet slot configuration
 * Server will extract cohort_id from player data (server-authoritative)
 * @param slotIndex - Which slot to clear (0-based)
 */
export async function removeFleetSlot(slotIndex: number): Promise<boolean> {
  const message = {
    type: MessageType.MessageTypeRemoveFleetSlot,
    slot_index: slotIndex,
  };

  console.log('[FleetService] Removing fleet slot:', slotIndex);

  return sendGatedCommand(OpCode.OpCodeRemoveFleetSlot, JSON.stringify(message), { notSent: (refusal) => handleFleetOperationFailed(refusal, 'Remove fleet slot') });
}

/**
 * Update a fleet slot's cabin layout and label. The sub-fleet keeps its
 * aircraft: an edit names none, and only a research upgrade changes the model
 * a sub-fleet flies (the server's UpgradeFleetSlots).
 * Server will extract cohort_id from player data (server-authoritative)
 * @param slotIndex - Which slot to update (0-based)
 * @param name - Player-authored label; empty is allowed
 * @param cabinConfig - New cabin layout and slot settings
 */
export async function updateFleetSlot(
  slotIndex: number,
  name: string,
  cabinConfig: CabinConfig
): Promise<boolean> {
  const message = {
    type: MessageType.MessageTypeUpdateFleetSlot,
    slot_index: slotIndex,
    name,
    cabin_config: cabinConfig,
  };

  console.log('[FleetService] Updating fleet slot', slotIndex);

  return sendGatedCommand(OpCode.OpCodeUpdateFleetSlot, JSON.stringify(message), { notSent: (refusal) => handleFleetOperationFailed(refusal, 'Update fleet slot') });
}

/**
 * Purchase airplanes for a specific fleet slot.
 * The aircraft bought is whatever the slot is configured for, so no model is sent.
 * Server will extract cohort_id from player data (server-authoritative)
 * @param slotIndex - Which slot to buy for (0-based)
 * @param quantity - Number of airplanes to purchase
 */
export async function purchaseAirplanes(
  slotIndex: number,
  quantity: number,
): Promise<boolean> {
  const message: Record<string, unknown> = {
    type: MessageType.MessageTypePurchaseAirplanes,
    slot_index: slotIndex,
    quantity: quantity,
  };

  console.log('[FleetService] Purchasing', quantity, 'airplanes for slot:', slotIndex);

  return sendGatedCommand(OpCode.OpCodePurchaseAirplanes, JSON.stringify(message), { notSent: handlePurchaseAirplanesFailed });
}

/**
 * Sell unassigned airplanes from a specific fleet slot
 * Server will extract cohort_id from player data (server-authoritative)
 * @param slotIndex - Which slot to sell from (0-based)
 * @param quantity - Number of airplanes to sell
 */
export async function sellAirplanes(
  slotIndex: number,
  quantity: number
): Promise<boolean> {
  const message = {
    type: MessageType.MessageTypeSellAirplanes,
    slot_index: slotIndex,
    quantity: quantity,
  };

  console.log('[FleetService] Selling', quantity, 'airplanes from slot:', slotIndex);

  return sendGatedCommand(OpCode.OpCodeSellAirplanes, JSON.stringify(message), { notSent: handleSellAirplanesFailed });
}
